import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpService } from 'src/app/core/data/service/http.service';
import { Auction } from '../schema/auction';
import { BankAccountInfo, StripeBankInfoResponse } from '../schema/bank-account-info';
import { StripeBalance, StripePayoutRequest, StripePayout } from '../schema/stripe-balance';
import { IStripeAccount } from '../schema/interfaces';

@Injectable({
  providedIn: 'root'
})
export class StripeConnectService extends HttpService {

  // this is set from the banking info call and means stripe will accept payments or not
  stripeChargesEnabled: boolean;

  // this is set from the banking info call and means stripe will process payouts or not
  stripePayoutsEnabled: boolean;

  // may as well store the stripe error string for quick access
  stripeErrors: string;

  constructor(http: HttpClient) {

    super(http);

    // Set the api endpoint, build default headers, and bind the events
    this.endpoint = `${this.apiURL}/stripeconnect`;
  }
  
  async getAuctionBankInfo() {
    const resp = new StripeBankInfoResponse((await super.fetch(`${this.endpoint}/stripebankinfo`) as StripeBankInfoResponse));
    this.stripeChargesEnabled = resp.chargesEnabled;
    this.stripePayoutsEnabled = resp.payoutsEnabled;
    this.stripeErrors = resp.message;
    return resp;
  }

  async getPartnerBankInfo() {
    return new BankAccountInfo((await super.fetch(`${this.endpoint}/partner/bankinfo`) as BankAccountInfo));
  }

  async getUncollectedPartnerFees() {
    return await super.fetch(`${this.endpoint}/partner/fees/uncollected`) as number;
  }

  async collectPartnerFees() {
    return await super.fetch(`${this.endpoint}/partner/fees/collect`) as number;
  }

  async createStripePartnerAccount(email: string): Promise<IStripeAccount> {
    const stripeAccountResp = await this.post({}, `${this.endpoint}/partner/account/create/${email}`) as IStripeAccount;
    return stripeAccountResp;
  }

  async getStripePartnerOnboardingLink(linkType: string): Promise<IStripeAccount> {
    const onboardingLinkProfile = await this.fetch(`${this.endpoint}/partner/onboardlink/${linkType}`) as IStripeAccount;
    return onboardingLinkProfile;
  }

  async getPartnerBalance() {
    return new StripeBalance((await super.fetch(`${this.endpoint}/partner/balance`) as StripeBalance));
  }

  async getPartnerPayouts() {
    return ((await super.fetch(`${this.endpoint}/partner/payouts`)) as []).map(m => new StripePayout(m));
  }


  async getStripeBalance(auctionId: number) {
    return new StripeBalance((await super.fetch(`${this.endpoint}/stripedepositbalance/${auctionId}`) as StripeBalance));
  }

  async startPayout(payoutRequest: StripePayoutRequest) {
    return new StripePayout((await this.post(payoutRequest, `${this.endpoint}/payouts`) as StripePayout));
  }

  async fetchPayouts(auctionId: number) {
    return ((await super.fetch(`${this.endpoint}/${auctionId}/stripepayouthistory`)) as []).map(m => new StripePayout(m));
  }
  
  async fetchPrevStripeAccounts(): Promise<IStripeAccount[]> {
    const stripeAccountResp = await this.fetch(`${this.endpoint}/paststripeaccounts`) as IStripeAccount[];
    return stripeAccountResp;
  }

  async createStripeAccount(auctionId: number, email: string): Promise<IStripeAccount> {
    const stripeAccountResp = await this.post({}, `${this.endpoint}/createstripeaccount/${auctionId}/${email}`) as IStripeAccount;
    return stripeAccountResp;
  }

  async getStripeOnboardingLink(auctionId: number, linkType: string): Promise<IStripeAccount> {
    const onboardingLinkProfile = await this.fetch(`${this.endpoint}/stripeonboardinglink/${auctionId}/${linkType}`) as IStripeAccount;
    return onboardingLinkProfile;
  }



  protected isUpdate(entity: Auction) {
    return false;
  }

  protected getUpdateUrl(entity: Auction, endpoint: string = this.endpoint) {
    return 'unimplemented';
  }
}
